import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, User, Check, X, AtSign } from 'lucide-react';
import { usePromptQueue, PromptPriority } from '@/contexts/PromptQueueContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  suggestUsername,
  normalizeUsername,
  fallbackHandleFromId,
  USERNAME_REGEX,
} from '@/lib/usernamePolicy';
import { invalidateUserProfile } from '@/lib/queryKeys';

export function NamePromptDialog() {
  const { user, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const invalidateProfileKeys = (uid: string) => invalidateUserProfile(queryClient, uid);

  const isOwnerRoute = location.pathname === '/owner-login' || location.pathname === '/owner' || location.pathname.startsWith('/owner/');

  const { data: isOwner } = useQuery({
    queryKey: ['is-owner', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'owner')
        .maybeSingle();
      return !!data;
    },
    enabled: !!user?.id,
    staleTime: Infinity,
  });

  const { requestPrompt, releasePrompt, isActivePrompt } = usePromptQueue();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [needsUsername, setNeedsUsername] = useState(false);
  const [usernameOnlyMode, setUsernameOnlyMode] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [hasCheckedProfile, setHasCheckedProfile] = useState(
    () => sessionStorage.getItem('name-prompt-checked') === 'true'
  );

  // Extract name from user_metadata (OAuth providers like Apple/Google)
  const extractNameFromMetadata = (user: any): { firstName: string | null; lastName: string | null } => {
    const meta = user?.user_metadata;
    if (!meta) return { firstName: null, lastName: null };

    let firstName = meta.first_name || null;
    let lastName = meta.last_name || null;

    // Try full_name
    if (!firstName && meta.full_name) {
      const parts = meta.full_name.trim().split(/\s+/);
      firstName = parts[0];
      lastName = parts.length > 1 ? parts.slice(1).join(' ') : null;
    }

    // Try name (Apple often uses this)
    if (!firstName && meta.name) {
      const parts = meta.name.trim().split(/\s+/);
      firstName = parts[0];
      lastName = parts.length > 1 ? parts.slice(1).join(' ') : null;
    }

    // Try provider_name
    if (!firstName && meta.provider_name) {
      const parts = meta.provider_name.trim().split(/\s+/);
      firstName = parts[0];
      lastName = parts.length > 1 ? parts.slice(1).join(' ') : null;
    }

    return { firstName, lastName };
  };

  // Check if user needs to set name (with settle delay to avoid post-login stampede)
  useEffect(() => {
    const checkProfile = async () => {
      if (authLoading || !user || hasCheckedProfile) return;
      
      // Skip on auth page
      if (location.pathname === '/auth') return;

      // Skip on owner routes and for users with the owner role
      if (isOwnerRoute) return;
      if (isOwner) return;

      // Honor a previous "Skip for now" dismissal
      if (localStorage.getItem('name-prompt-dismissed') === 'true') return;
      
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('first_name, last_name, username')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error('Error checking profile:', error);
        return;
      }
      
      setHasCheckedProfile(true);
      sessionStorage.setItem('name-prompt-checked', 'true');
      
      // The last name is optional, so only a missing first name asks. A first
      // name is never copied into the last name.
      if (!profile?.first_name) {
        const { firstName: metaFirst, lastName: metaLast } = extractNameFromMetadata(user);
        
        if (metaFirst) {
          // Auto-save from OAuth metadata — never show the prompt
          const displayName = `${metaFirst}${metaLast ? ' ' + metaLast : ''}`;
          const { error: updateError } = await supabase
            .from('profiles')
            .update({
              first_name: profile?.first_name || metaFirst,
              last_name: profile?.last_name || metaLast || null,
              display_name: displayName,
              updated_at: new Date().toISOString()
            })
            .eq('id', user.id);
          
          if (updateError) {
            console.error('Error auto-saving name from metadata:', updateError);
          } else {
            invalidateProfileKeys(user.id);
          }
          // Successfully auto-saved (or failed silently) — don't show dialog
          return;
        }
        
        // Check if this is an Apple Sign In user — never prompt them (App Store Guideline 4.0)
        const isAppleUser = user?.app_metadata?.provider === 'apple' || 
          (user?.app_metadata?.providers as string[] | undefined)?.includes('apple');
        
        if (isAppleUser) {
          // App Store Guideline 4.0 — never *require* a real name from Apple
          // Sign In users. If they already picked a username, do nothing.
          // Otherwise open the dialog in username-only mode with a suggestion.
          if (profile?.username) {
            return;
          }
          const suggestion = await suggestUsername(supabase, '', '');
          setUsername(suggestion);
          setUsernameOnlyMode(true);
          setNeedsUsername(true);
          if (location.pathname !== '/') {
            requestPrompt('name', PromptPriority.NAME);
          }
          return;
        }

        
        // No metadata available — show the prompt (email/password signups only)
        setNeedsUsername(!profile?.username);
        if (user?.id && location.pathname !== '/') {
          requestPrompt('name', PromptPriority.NAME);
        }
      }
    };

    const settleTimer = setTimeout(checkProfile, 1500);
    return () => clearTimeout(settleTimer);
  }, [user, authLoading, hasCheckedProfile, location.pathname, isOwnerRoute, isOwner]);

  // Check username availability with debounce
  useEffect(() => {
    if (!needsUsername || !username || username.length < 3) {
      setIsAvailable(null);
      return;
    }

    // Validate format
    const usernameRegex = /^[a-zA-Z0-9._]+$/;
    if (!usernameRegex.test(username)) {
      setIsAvailable(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsChecking(true);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username.toLowerCase())
        .maybeSingle();
      
      if (error) {
        console.error('Error checking username:', error);
        setIsAvailable(null);
      } else {
        setIsAvailable(data === null);
      }
      
      setIsChecking(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [username, needsUsername]);

  const handleSave = async () => {
    if (!user) return;
    if (usernameOnlyMode) {
      if (!username || username.length < 3 || !isAvailable) return;
      if (!USERNAME_REGEX.test(username)) return;
    } else {
      if (!firstName.trim()) return;
      // If username is provided but invalid, don't proceed
      if (needsUsername && username && username.length >= 3) {
        if (!USERNAME_REGEX.test(username) || !isAvailable) return;
      }
    }

    setIsSaving(true);

    const updateData: Record<string, string | null> = usernameOnlyMode
      ? {
          username: normalizeUsername(username),
          updated_at: new Date().toISOString(),
        }
      : {
          first_name: firstName.trim(),
          last_name: lastName.trim() || null,
          display_name: `${firstName.trim()} ${lastName.trim()}`.trim(),
          updated_at: new Date().toISOString(),
        };

    // Non-username-only flow: include the optional username when valid.
    if (!usernameOnlyMode && needsUsername && username && username.length >= 3 && isAvailable) {
      updateData.username = normalizeUsername(username);
    }

    const { error } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', user.id);

    if (error) {
      if (error.code === '23505') {
        toast({
          title: 'Username taken',
          description: 'This username was just claimed. Please try another.',
          variant: 'destructive',
        });
        setIsAvailable(false);
      } else {
        toast({
          title: 'Error saving profile',
          description: error.message,
          variant: 'destructive',
        });
      }
    } else {
      toast({
        title: usernameOnlyMode ? 'Username set!' : 'Welcome!',
        description: usernameOnlyMode
          ? `You're all set, @${normalizeUsername(username)}!`
          : `Nice to meet you, ${firstName.trim()}!`,
      });
      invalidateProfileKeys(user.id);
      releasePrompt('name');
    }

    setIsSaving(false);
  };

  const handleSkip = async () => {
    if (usernameOnlyMode && user?.id) {
      // Persist a deterministic fallback handle so this user never renders as
      // "Grounded M." in the feed again. 23505 collisions are extremely rare;
      // even if the write fails, FeedCard's display-time fallback still shows
      // the same handle for this user id.
      const handle = fallbackHandleFromId(user.id);
      const { error: skipError } = await supabase
        .from('profiles')
        .update({ username: handle, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (!skipError) invalidateProfileKeys(user.id);
      releasePrompt('name');
      localStorage.setItem('name-prompt-dismissed', 'true');
      toast({
        title: 'Skipped for now',
        description: 'You can pick a username anytime in your profile.',
      });
      return;
    }
    releasePrompt('name');
    localStorage.setItem('name-prompt-dismissed', 'true');
    toast({
      title: 'Skipped for now',
      description: 'You can set your name anytime in your profile.',
    });
  };

  const isFirstNameValid = firstName.trim().length >= 1 && firstName.trim().length <= 50;
  const isLastNameValid = lastName.trim().length <= 50;
  const isUsernameValid = !username || username.length < 3 || (username.length >= 3 && USERNAME_REGEX.test(username) && isAvailable);
  const canSubmit = usernameOnlyMode
    ? (username.length >= 3 && USERNAME_REGEX.test(username) && isAvailable === true && !isSaving)
    : (isFirstNameValid && isLastNameValid && isUsernameValid && !isSaving);


  if (isOwnerRoute || isOwner) return null;

  return (
    <Dialog open={isActivePrompt('name')} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {usernameOnlyMode ? (
              <AtSign className="h-5 w-5 text-primary" />
            ) : (
              <User className="h-5 w-5 text-primary" />
            )}
            {usernameOnlyMode ? 'Pick a username' : "Welcome! Let's get to know you"}
          </DialogTitle>
          <DialogDescription>
            {usernameOnlyMode
              ? 'Choose a handle so the community can recognize you. You can change it later.'
              : 'Tell us your name so the community can recognize you.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {!usernameOnlyMode && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First name *</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  maxLength={50}
                  autoComplete="given-name"
                />
                {firstName && !isFirstNameValid && (
                  <p className="text-xs text-destructive">Required (1-50 characters)</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last name (optional)</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name (optional)"
                  maxLength={50}
                  autoComplete="family-name"
                />
                {lastName && !isLastNameValid && (
                  <p className="text-xs text-destructive">Up to 50 characters</p>
                )}
              </div>
            </div>
          )}

          {needsUsername && (
            <div className="space-y-2">
              <Label htmlFor="username">{usernameOnlyMode ? 'Username' : 'Username (optional)'}</Label>

              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))}
                  placeholder="your_username"
                  className="pl-8 pr-10"
                  maxLength={30}
                  autoComplete="off"
                />
                {username.length >= 3 && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {isChecking ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : isAvailable === true ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : isAvailable === false ? (
                      <X className="h-4 w-4 text-destructive" />
                    ) : null}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                3-30 characters. Letters, numbers, dots, and underscores only.
              </p>
              {username.length >= 3 && !isChecking && (
                <p className={`text-xs ${isAvailable ? 'text-green-600' : 'text-destructive'}`}>
                  {isAvailable 
                    ? '✓ Username is available' 
                    : !/^[a-zA-Z0-9._]+$/.test(username)
                      ? 'Invalid characters. Use only letters, numbers, dots, and underscores.'
                      : 'Username is already taken'}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={handleSkip} disabled={isSaving}>
            Skip for now
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSubmit}
            className="w-full sm:w-auto"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
